import { DepartmentContext } from 'DepartmentWrapper';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import React, { useState, useContext } from 'react';
import { Button, Card, CardBody, CardHeader, Input, InputGroup, Table } from 'reactstrap';
import style from './MergeUsers.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { fetchDataAgnostic, postDataAgnostic } from 'Services/dataApi';
import { checkLoginForError, formatBearerToken } from 'Common.functions';
import Loading from 'Body/Statuses/Loading';
import Swal from 'sweetalert2';

const MERGE_USER_STEPS = {
  NO_ISSUE: 'NO_ISSUE',
  BAD_USER_ONLY: 'BAD_USER_ONLY',
  BOTH_USERS_FOUND: 'BOTH_USERS_FOUND',
};

const MergeUsers = ({ onSearch, selectDepartment, setShowSuperUserPanel, selectRole }) => {
  const [badEmail, setBadEmail] = useState('');
  const [goodEmail, setGoodEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedGoodUser, setSelectedGoodUser] = useState(null);
  const [selectedBadUser, setSelectedBadUser] = useState(null);
  const [mergeAnalysis, setMergeAnalysis] = useState(null);

  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const dContext = useContext(DepartmentContext);

  const handleUserSelect = (user, type) => {
    if (type === 'good') {
                           setSelectedGoodUser(user);
                           setGoodEmail(user.Auth0Email); // Update the goodEmail state when selecting from table
                         } else {
      setSelectedBadUser(user);
      // Always use good email if no good user is selected
      if (!selectedGoodUser) {
        setSelectedGoodUser({
          Auth0Email: goodEmail,
          UserFirst: '',
          UserLast: '',
          pk_User: null,
        });
      }
    }
  };

  const handleMergeAnalysis = async () => {
    try {
      setIsLoading(true);
      const mergeUserStep = selectedGoodUser?.pk_User
        ? MERGE_USER_STEPS.BOTH_USERS_FOUND
        : MERGE_USER_STEPS.BAD_USER_ONLY;

      const response = await fetchDataAgnostic(
        `organization/users/merge-users/analysis`,
        {
          good_user_email: selectedGoodUser?.Auth0Email,
          bad_user_email: selectedBadUser?.Auth0Email,
          merge_user_step: mergeUserStep,
        },
        formatBearerToken(await getTokenSilently()),
      );

      if (response.error) {
        throw new Error(response.message || 'Error during merge analysis');
      }

      const { bad_user_data, good_user_data } = response.data;

      // Format analysis results
      const formatAnalysis = (userEmail, userData, userType) => {
        if (
          !userData ||
          (!userData.auth0_data &&
            !userData.user_details &&
            !userData.affected_tables_with_row_count &&
            !userData.user_departments &&
            !userData.merge_analysis)
        ) {
          return `<h4>${userType} User Analysis (${userEmail}):</h4><p>No data available</p>`;
        }

        const auth0Data = userData.auth0_data || {};
        const mergeAnalysis = userData.merge_analysis || [];
        const affectedTables = userData.affected_tables_with_row_count || [];
        const user = userData.user_details;
        const userDepartments = userData.user_departments || [];

        return `
      <div class="analysis-section">
        <h4>${userType} User Details (${userEmail}):</h4>
        ${
          user && Object.keys(user).length > 0
            ? `
          <div class="user-details">
            <p><strong>Email:</strong> ${user.Auth0Email || 'N/A'}</p>
            <p><strong>Name:</strong> ${user.UserFirst} ${user.UserLast}</p>
            <p><strong>User ID:</strong> ${user.pk_User || 'N/A'}</p>
          </div>
        `
            : '<p>No user found in database</p>'
        }

        ${
          userDepartments && userDepartments.length > 0
            ? `
          <div class="user-departments">
            <p><strong>Departments:</strong></p>
            <ul>
              ${userDepartments
                .map(
                  (department) => `
                    <li>${department.DepartmentName} (${department.OrganizationName})</li>
                  `,
                )
                .join('')}
            </ul>
          </div>
        `
            : ''
        }
  
        ${
          auth0Data && Object.keys(auth0Data).length > 0
            ? `
          <h5>Auth0 Information:</h5>
          <div class="auth0-details">
            <p><strong>Last Login:</strong> ${
              auth0Data.last_login ? new Date(auth0Data.last_login).toLocaleString() : 'N/A'
            }</p>
            <p><strong>Login Count:</strong> ${auth0Data.logins_count || 0}</p>
            <p><strong>Created:</strong> ${
              auth0Data.created_at ? new Date(auth0Data.created_at).toLocaleString() : 'N/A'
            }</p>
          </div>
        `
            : ''
        }
  
        ${
          affectedTables.length > 0
            ? `
          <h5>Affected Database Tables:</h5>
          <div class="affected-tables">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Table Name</th>
                  <th>Affected Rows</th>
                </tr>
              </thead>
              <tbody>
                ${affectedTables
                  .map(
                    (table) => `
                  <tr>
                    <td>${table.table}</td>
                    <td>${table.affected_rows}</td>
                  </tr>
                `,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        `
            : ''
        }
  
        ${
          mergeAnalysis.length > 0
            ? `
          <h5>Merge Actions:</h5>
          <div class="merge-actions">
            ${mergeAnalysis
              .map(
                (action) => `
              <p class="action-item">• ${action.message}</p>
            `,
              )
              .join('')}
          </div>
        `
            : ''
        }
      </div>
    `;
      };

      const badUserHtml = formatAnalysis(selectedBadUser.Auth0Email, bad_user_data, 'Bad');
      const goodUserHtml = formatAnalysis(selectedGoodUser.Auth0Email, good_user_data, 'Good');

      const mergeAnalysisHtml = `
<style>
  .merge-analysis-popup {
    max-width: 90vw !important;
  }
  .analysis-section {
    text-align: left;
    margin-bottom: 1.5rem;
    min-width: 600px;
  }
  .merge-type {
    background-color: #e9ecef;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    text-align: center;
    font-weight: bold;
  }
  .user-details, .auth0-details, .affected-tables, .merge-actions {
    padding: 1rem;
    margin: 0.5rem 0;
    background-color: #f8f9fa;
    border-radius: 4px;
  }
  .table {
    margin: 0;
    font-size: 0.9rem;
    width: 100%;
  }
  .table td, .table th {
    padding: 0.5rem 1rem;
  }
  .action-item {
    margin: 0.25rem 0;
    color: #666;
  }
  h5 {
    margin-top: 1rem;
    color: #495057;
  }
</style>
<div class="text-left">
  <div class="merge-type">
    Merge Type: ${mergeUserStep === MERGE_USER_STEPS.BOTH_USERS_FOUND ? 'Both Users Found' : 'Bad User Only'}
  </div>
  ${badUserHtml}
  <hr />
  ${goodUserHtml}
</div>
`;

      // Create the analysis data object
      const analysisData = {
        good_user_email: selectedGoodUser?.Auth0Email,
        bad_user_email: selectedBadUser?.Auth0Email,
        merge_user_step: selectedGoodUser?.pk_User ? MERGE_USER_STEPS.BOTH_USERS_FOUND : MERGE_USER_STEPS.BAD_USER_ONLY,
        bad_user_data,
        good_user_data,
      };

      // Save to state for future reference
      setMergeAnalysis(analysisData);

      console.log('Analysis data prepared:', analysisData);

      const result = await Swal.fire({
        title: 'Merge Analysis',
        html: mergeAnalysisHtml,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Proceed with Merge',
        cancelButtonText: 'Cancel',
        width: '800px',
        customClass: {
          container: 'merge-analysis-modal',
          popup: 'merge-analysis-popup',
        },
        preConfirm: async () => {
          // Update the popup to show loading state
          Swal.update({
            title: 'Executing Merge',
            html: '<div class="text-center">Please wait while the merge is being processed...</div>',
            icon: 'info',
            showConfirmButton: false,
            showCancelButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
          });
          Swal.showLoading();

          // Execute the merge
          try {
            await handleMergeProcess(analysisData);
            return true;
          } catch (error) {
            Swal.showValidationMessage(error.message || 'Failed to complete merge process');
            return false;
          }
        },
      });
    } catch (error) {
      console.error('Merge analysis error:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to analyze merge',
        icon: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMergeProcess = async (analysisData) => {
    try {
      setIsLoading(true);
      console.log('Executing merge with analysis:', analysisData);

      const mergeResponse = await postDataAgnostic(
        'organization/users/merge-users/execute',
        {}, // query params
        analysisData || mergeAnalysis, // request body
        formatBearerToken(await getTokenSilently()),
      );

      if (mergeResponse.error) {
        throw new Error(mergeResponse.message || 'Error during merge process');
      }

      // Show success message
      await Swal.fire({
        icon: 'success',
        title: 'Merge Completed',
        text: mergeResponse.message || 'Users have been successfully merged',
        confirmButtonText: 'OK',
      });

      // Clear the form
      setGoodEmail('');
      setBadEmail('');
      setSelectedGoodUser(null);
      setSelectedBadUser(null);
      setSearchResults(null);
      setMergeAnalysis(null);
    } catch (error) {
      console.error('Merge process error:', error);
      throw error; // Let preConfirm handle the error
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmails = () => {
    if (!badEmail || !goodEmail) {
      throw new Error('Please provide both email addresses');
    }
    if (badEmail === goodEmail) {
      throw new Error('Bad and good email addresses must be different');
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(badEmail) || !emailRegex.test(goodEmail)) {
      throw new Error('Please provide valid email addresses');
    }
  };

  const handleSearch = async () => {
    try {
          setIsLoading(true);
          validateEmails();

          const token = await getTokenSilently();

          // Call the search endpoint with switched order
          const response = await postDataAgnostic(
            'organization/users/merge-users/search',
            {},
            {
              bad_user_email: badEmail,
              good_user_email: goodEmail,
            },
            formatBearerToken(token),
          );

          if (response.error) {
            throw new Error(response.message || 'Error searching users');
          }

          setSearchResults(response.data);

          console.log(response);
          // If no matches found
          if (!response.data.bad_users.length && !response.data.good_users.length) {
            await Swal.fire({
              icon: 'info',
              title: 'No Users Found',
              text: 'No users found matching the provided email addresses.',
            });
            return;
          }
        } catch (err) {
      console.error('Search error:', err);

      if (err.message === 'Login required' || err.message === 'Unauthorized') {
        checkLoginForError({ err, loginWithRedirect });
        return;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Error searching users',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeselect = (type) => {
    if (type === 'good') {
      // When deselecting good user, revert to using just the email
      setSelectedGoodUser({
        Auth0Email: goodEmail,
        UserFirst: '',
        UserLast: '',
        pk_User: null,
      });
    } else {
      setSelectedBadUser(null);
    }
  };

  const renderEmptyTable = (title) => (
    <div className="mt-4">
      <h5>{title}</h5>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Email</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>User ID</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="4" className="text-center text-muted">
              Search to view matching users
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );

  const renderUserTable = (users, title, type) => {
    return (
      <div className="mt-4">
        {type === 'good' && <p className="mb-2">Select a user from the tables below to begin the merge process.</p>}
        <h5>{title}</h5>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Email</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>User ID</th>
            </tr>
          </thead>
          <tbody>
            {!users || users.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  Search to view matching users
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.pk_User}
                  onClick={() => handleUserSelect(user, type)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      (type === 'good' && selectedGoodUser?.pk_User === user.pk_User) ||
                      (type === 'bad' && selectedBadUser?.pk_User === user.pk_User)
                        ? '#e6f3ff'
                        : undefined,
                  }}
                >
                  <td>{user.Auth0Email}</td>
                  <td>{user.UserFirst}</td>
                  <td>{user.UserLast}</td>
                  <td>{user.pk_User}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderSelectedUsers = () => (
    <div className="mt-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
      <h5>Selected Users for Merge</h5>

      <div style={{ ...style.simpleColumn, gap: '1rem' }}>
        <div style={{ ...style.simpleRow, alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '120px' }}>Bad User:</div>
          <InputGroup>
            <Input
              type="text"
              value={
                selectedBadUser
                  ? `${selectedBadUser.Auth0Email} (${selectedBadUser.UserFirst} ${selectedBadUser.UserLast})`
                  : ''
              }
              disabled
              placeholder="Select a user from the Bad Users table"
            />
            {selectedBadUser && (
              <Button color="secondary" onClick={() => handleDeselect('bad')} style={{ marginLeft: '0.5rem' }}>
                Deselect
              </Button>
            )}
          </InputGroup>
        </div>

        <div style={{ ...style.simpleRow, alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '120px' }}>Good User:</div>
          <InputGroup>
            <Input
              type="email"
              value={
                selectedGoodUser
                  ? selectedGoodUser.pk_User
                    ? `${selectedGoodUser.Auth0Email} (${selectedGoodUser.UserFirst} ${selectedGoodUser.UserLast})`
                    : selectedGoodUser.Auth0Email
                  : goodEmail
              }
              onChange={(e) => {
                const newEmail = e.target.value;
                setGoodEmail(newEmail);
                // When manually entering email, reset to email-only mode
                setSelectedGoodUser({
                  Auth0Email: newEmail,
                  UserFirst: '',
                  UserLast: '',
                  pk_User: null,
                });
              }}
              placeholder="Enter good email or select from Good Users table"
            />
            {selectedGoodUser && selectedGoodUser.pk_User && (
              <Button color="secondary" onClick={() => handleDeselect('good')} style={{ marginLeft: '0.5rem' }}>
                Deselect
              </Button>
            )}
          </InputGroup>
        </div>

        <div style={{ ...style.simpleRow, justifyContent: 'center', marginTop: '1rem' }}>
          <Button color="warning" onClick={handleMergeAnalysis} disabled={!selectedBadUser} style={{ width: '200px' }}>
            {isLoading ? (
              <Loading options={{ labelText: 'Analyzing...' }} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem' }} />
                Run Merge Analysis
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loading options={{ labelText: 'Please wait...' }} />
        </div>
      )}
      <Card>
        <CardHeader>Merge Users</CardHeader>
        <CardBody>
          <div style={{ ...style.simpleColumn, gap: '1rem', pointerEvents: isLoading ? 'none' : 'auto' }}>
            {/* Search inputs - Bad Email first */}
            <div style={{ ...style.simpleRow, alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '120px' }}>Bad Email:</div>
              <InputGroup>
                <Input
                  type="email"
                  value={badEmail}
                  onChange={(e) => setBadEmail(e.target.value)}
                  placeholder="Enter bad email address"
                  disabled={isLoading}
                />
              </InputGroup>
            </div>

            <div style={{ ...style.simpleRow, alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '120px' }}>Good Email:</div>
              <InputGroup>
                <Input
                  type="email"
                  value={goodEmail}
                  onChange={(e) => setGoodEmail(e.target.value)}
                  placeholder="Enter good email address"
                  disabled={isLoading}
                />
              </InputGroup>
            </div>

            <div style={{ ...style.simpleRow, justifyContent: 'center', marginTop: '1rem' }}>
              <Button color="primary" onClick={handleSearch} disabled={isLoading} style={{ width: '150px' }}>
                {isLoading ? (
                  <Loading options={{ labelText: 'Searching...' }} />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem' }} />
                    Search Users
                  </>
                )}
              </Button>
            </div>

            {/* User Tables */}
            {renderUserTable(searchResults?.bad_users, 'Bad Users Found', 'bad')}
            {renderUserTable(searchResults?.good_users, 'Good Users Found', 'good')}
            {renderSelectedUsers()}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default MergeUsers;
